import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  MenuItem,
  Fade,
  Grow,
  Paper,
  Divider,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  Skeleton,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'; 
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import VideocamIcon from '@mui/icons-material/Videocam';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';
import { useClubTheme } from '../contexts/ClubThemeContext';
import AgeAtInformeChip from './AgeAtInformeChip';
import InformeClipsSection from './InformeClipsSection';
import DefensiveVersatilityChart from './DefensiveVersatility/DefensiveVersatilityChart';
import DefensiveVersatilityEditor from './DefensiveVersatility/DefensiveVersatilityEditor';
import DocumentMediaDialog from './DocumentMediaDialog';
import InformeHtmlThumbnail from './InformeHtmlThumbnail';
import {
  getInformeMediaUrl,
  isImageMedia,
  isPdfMedia,
  isHtmlMedia,
  isHtmlFile,
  isAcceptedMedia,
  asMediaDoc,
  MAX_MEDIA_SIZE,
  MEDIA_ACCEPT_ATTR
} from '../utils/informeMedia';
import { normalizeVersatility, hasAnyValue } from '../utils/versatility';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  '&:last-child': {
    marginBottom: 0
  },
}));

const StyledChip = styled(Chip)(({ theme, variant }) => ({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: '12px',
  ...(variant === 'scouting' && {
    backgroundColor: theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.1)
      : alpha(theme.palette.primary.main, 0.2),
    color: theme.palette.primary.main,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  }),
  ...(variant === 'nivel' && {
    backgroundColor: theme.palette.mode === 'light'
      ? alpha(theme.palette.secondary.main, 0.1)
      : alpha(theme.palette.secondary.main, 0.2),
    color: theme.palette.secondary.main,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
  }),
  ...(variant === 'projection' && {
    backgroundColor: theme.palette.mode === 'light'
      ? alpha(theme.palette.success.main, 0.1)
      : alpha(theme.palette.success.main, 0.2),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.grey[50]
    : alpha(theme.palette.background.paper, 0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.7,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(0.5),
  verticalAlign: 'middle',
  color: theme.palette.text.secondary,
}));

// API functions
const fetchTeams = async () => {
  try {
    const res = await api.get('/api/players/teams');
    return res.data?.teams || [];
  } catch (error) {
    console.error('Error fetching teams:', error);
    return [];
  }
};

const fetchEventos = async () => {
  try {
    const res = await api.get('/api/informe/eventos');
    return res.data || [];
  } catch (error) {
    console.error('Error fetching eventos:', error);
    return [];
  }
};

const fetchExistingEquipos = async () => {
  try {
    const res = await api.get('/api/informe/equipos');
    return res.data || [];
  } catch (error) {
    console.error('Error fetching existing equipos:', error);
    return [];
  }
};

// Helper functions
const getAvatarUrl = (userId) => {
  return `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`;
};

const getScoutingIcon = (visu) => {
  return visu === 'video' ? <VideocamIcon fontSize="small" /> : <RemoveRedEyeIcon fontSize="small" />;
};

const getScoutingMethod = (visu) => {
  return visu === 'video' ? 'Video' : visu === 'live' ? 'Live' : '';
};

// Helper function to translate nivel/projection values
// Supports both old Spanish values from DB and new translation keys
const getTranslatedLevel = (value, t) => {
  if (!value) return '';

  // Mapping of DB values to translation keys
  // Supports old Spanish values, Spanish short keys, AND English short keys from CreateInforme
  const levelKeyMap = {
    // Proyectos - old values
    'Proyecto Nivel Bajo': 'playerCardCreator.informeForm.levels.proyectoBajo',
    'Proyecto Nivel Medio': 'playerCardCreator.informeForm.levels.proyectoMedio',
    'Proyecto Nivel Bueno': 'playerCardCreator.informeForm.levels.proyectoBueno',
    'Proyecto Top': 'playerCardCreator.informeForm.levels.proyectoTop',
    'Futura Estrella': 'playerCardCreator.informeForm.levels.futuraEstrella',
    // Proyectos - Spanish short keys
    'proyectoBajo': 'playerCardCreator.informeForm.levels.proyectoBajo',
    'proyectoMedio': 'playerCardCreator.informeForm.levels.proyectoMedio',
    'proyectoBueno': 'playerCardCreator.informeForm.levels.proyectoBueno',
    'proyectoTop': 'playerCardCreator.informeForm.levels.proyectoTop',
    'futuraEstrella': 'playerCardCreator.informeForm.levels.futuraEstrella',
    // Proyectos - English short keys (from CreateInforme.js)
    'lowLevelProspect': 'createPlayer.levels.lowLevelProspect',
    'midLevelProspect': 'createPlayer.levels.midLevelProspect',
    'goodLevelProspect': 'createPlayer.levels.goodLevelProspect',
    'topProspect': 'createPlayer.levels.topProspect',
    'futureStar': 'createPlayer.levels.futureStar',
    // EBA - old values
    'EBA Nivel Bajo (10-20)': 'playerCardCreator.informeForm.levels.ebaBajo',
    'EBA Nivel Alto (20-30)': 'playerCardCreator.informeForm.levels.ebaAlto',
    // EBA - Spanish short keys
    'ebaBajo': 'playerCardCreator.informeForm.levels.ebaBajo',
    'ebaAlto': 'playerCardCreator.informeForm.levels.ebaAlto',
    // EBA - English short keys (from CreateInforme.js)
    'ebaLow': 'createPlayer.levels.ebaLow',
    'ebaHigh': 'createPlayer.levels.ebaHigh',
    // LEB - old values
    'LEB Nivel Bajo (30-40)': 'playerCardCreator.informeForm.levels.lebBajo',
    'LEB Nivel Alto (40-50)': 'playerCardCreator.informeForm.levels.lebAlto',
    // LEB - Spanish short keys
    'lebBajo': 'playerCardCreator.informeForm.levels.lebBajo',
    'lebAlto': 'playerCardCreator.informeForm.levels.lebAlto',
    // LEB - English short keys (from CreateInforme.js)
    'lebLow': 'createPlayer.levels.lebLow',
    'lebHigh': 'createPlayer.levels.lebHigh',
    // ACB - old values
    'ACB Rotación (50-60)': 'playerCardCreator.informeForm.levels.acbRotacion',
    // ACB - Spanish short keys
    'acbRotacion': 'playerCardCreator.informeForm.levels.acbRotacion',
    // ACB - English short keys (from CreateInforme.js)
    'acbRotation': 'createPlayer.levels.acbRotation',
    // BCL/Eurocup - old values
    'Bottom BCL/Eurocup (60-65)': 'playerCardCreator.informeForm.levels.bottomBcl',
    'Mid BCL/Eurocup (65-70)': 'playerCardCreator.informeForm.levels.midBcl',
    'Top BCL/Eurocup (70-75)': 'playerCardCreator.informeForm.levels.topBcl',
    // BCL/Eurocup - Spanish short keys
    'bottomBcl': 'playerCardCreator.informeForm.levels.bottomBcl',
    'midBcl': 'playerCardCreator.informeForm.levels.midBcl',
    'topBcl': 'playerCardCreator.informeForm.levels.topBcl',
    // BCL/Eurocup - English short keys (from CreateInforme.js)
    'bottomBclEurocup': 'createPlayer.levels.bottomBclEurocup',
    'midBclEurocup': 'createPlayer.levels.midBclEurocup',
    'topBclEurocup': 'createPlayer.levels.topBclEurocup',
    // Euroliga - old values
    'EL Nivel Medio (75-80)': 'playerCardCreator.informeForm.levels.elMedio',
    'EL Top (80-85)': 'playerCardCreator.informeForm.levels.elTop',
    // Euroliga - Spanish short keys
    'elMedio': 'playerCardCreator.informeForm.levels.elMedio',
    // Euroliga - English short keys (from CreateInforme.js)
    'elMid': 'createPlayer.levels.elMid',
    'elTop': 'createPlayer.levels.elTop',
    // NBA - old values
    'NBA Rotación (85-90)': 'playerCardCreator.informeForm.levels.nbaRotacion',
    'NBA All-Star (95-100)': 'playerCardCreator.informeForm.levels.nbaAllStar',
    // NBA - Spanish short keys
    'nbaRotacion': 'playerCardCreator.informeForm.levels.nbaRotacion',
    // NBA - English short keys (from CreateInforme.js)
    'nbaRotation': 'createPlayer.levels.nbaRotation',
    'nbaAllStar': 'createPlayer.levels.nbaAllStar',
    // College - old values
    'College Nivel Bajo': 'playerCardCreator.informeForm.levels.collegeBajo',
    'College Nivel Medio': 'playerCardCreator.informeForm.levels.collegeMedio',
    'College Top': 'playerCardCreator.informeForm.levels.collegeTop',
    // College - Spanish short keys
    'collegeBajo': 'playerCardCreator.informeForm.levels.collegeBajo',
    'collegeMedio': 'playerCardCreator.informeForm.levels.collegeMedio',
    'collegeTop': 'playerCardCreator.informeForm.levels.collegeTop',
    // College - English short keys (from CreateInforme.js)
    'collegeLow': 'createPlayer.levels.collegeLow',
    'collegeMid': 'createPlayer.levels.collegeMid',
  };

  const translationKey = levelKeyMap[value];
  if (translationKey) {
    return t(translationKey);
  }

  // Fallback: return the raw value if no mapping found
  return value;
};

// Helper function to normalize DB value to the new English key format
// Maps old Spanish values and Spanish short keys to English keys used in select options
const normalizeToSelectValue = (value) => {
  if (!value) return '';

  // Mapping from old values to new English keys
  const oldValueToNewKey = {
    // Old Spanish full values -> English keys
    'Proyecto Nivel Bajo': 'lowLevelProspect',
    'Proyecto Nivel Medio': 'midLevelProspect',
    'Proyecto Nivel Bueno': 'goodLevelProspect',
    'Proyecto Top': 'topProspect',
    'Futura Estrella': 'futureStar',
    'EBA Nivel Bajo (10-20)': 'ebaLow',
    'EBA Nivel Alto (20-30)': 'ebaHigh',
    'LEB Nivel Bajo (30-40)': 'lebLow',
    'LEB Nivel Alto (40-50)': 'lebHigh',
    'ACB Rotación (50-60)': 'acbRotation',
    'Bottom BCL/Eurocup (60-65)': 'bottomBclEurocup',
    'Mid BCL/Eurocup (65-70)': 'midBclEurocup',
    'Top BCL/Eurocup (70-75)': 'topBclEurocup',
    'EL Nivel Medio (75-80)': 'elMid',
    'EL Top (80-85)': 'elTop',
    'NBA Rotación (85-90)': 'nbaRotation',
    'NBA All-Star (95-100)': 'nbaAllStar',
    'College Nivel Bajo': 'collegeLow',
    'College Nivel Medio': 'collegeMid',
    'College Top': 'collegeTop',
    // Old Spanish short keys -> English keys
    'proyectoBajo': 'lowLevelProspect',
    'proyectoMedio': 'midLevelProspect',
    'proyectoBueno': 'goodLevelProspect',
    'proyectoTop': 'topProspect',
    'futuraEstrella': 'futureStar',
    'ebaBajo': 'ebaLow',
    'ebaAlto': 'ebaHigh',
    'lebBajo': 'lebLow',
    'lebAlto': 'lebHigh',
    'acbRotacion': 'acbRotation',
    'bottomBcl': 'bottomBclEurocup',
    'midBcl': 'midBclEurocup',
    'topBcl': 'topBclEurocup',
    'elMedio': 'elMid',
    'nbaRotacion': 'nbaRotation',
    'collegeBajo': 'collegeLow',
    'collegeMedio': 'collegeMid',
  };

  // If it's an old value, return the new English key
  if (oldValueToNewKey[value]) {
    return oldValueToNewKey[value];
  }

  // Otherwise return as-is (it's already a new English key)
  return value;
};

// Loading skeleton component
const InformeSkeleton = () => (
  <Card sx={{ mb: 2, borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={80} height={24} />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>
    </CardContent>
  </Card>
);

const InformeTab = ({ playerId, dateNaissance, onDataLoaded }) => {
  const { t } = useTranslation();
  const { clubName } = useClubTheme();
  const [informeData, setInformeData] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));

  // Edit state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentInforme, setCurrentInforme] = useState(null);
  const [editedDetalles, setEditedDetalles] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedEvento, setEditedEvento] = useState('');
  const [editedEquipo, setEditedEquipo] = useState('');
  const [editedVisu, setEditedVisu] = useState('');
  const [editedNivel, setEditedNivel] = useState('');
  const [editedProjection, setEditedProjection] = useState('');
  const [editedMediaFile, setEditedMediaFile] = useState(null);
  const [editedMediaPreviewUrl, setEditedMediaPreviewUrl] = useState(null);
  const [removeExistingMedia, setRemoveExistingMedia] = useState(false);
  const [editedFitEstilo, setEditedFitEstilo] = useState(null);
  const [editedFitFisico, setEditedFitFisico] = useState(null);
  const [editedFitActitud, setEditedFitActitud] = useState(null);
  const [editedFitMejora, setEditedFitMejora] = useState(null);
  const [editedFitContexto, setEditedFitContexto] = useState(null);
  const [editedStatsUrl, setEditedStatsUrl] = useState('');
  const [editedDefensiveVersatility, setEditedDefensiveVersatility] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsDialogUrl, setStatsDialogUrl] = useState('');
  const [iframeError, setIframeError] = useState(false);
  const editFileInputRef = React.useRef(null);

  // Delete state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [informeToDelete, setInformeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Media preview state
  const [mediaPreview, setMediaPreview] = useState({ open: false, url: '', filename: '', informeId: null });
  // Pièce jointe HTML : aperçu in-app dans une iframe sandbox (même dialog que les documents)
  const [htmlPreview, setHtmlPreview] = useState(null);

  // Autocomplete data
  const [teams, setTeams] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [existingEquipos, setExistingEquipos] = useState([]);

  // No longer needed - removed competition selection state

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // VBC Rating keys - same as CreateInforme.js for consistency
  const vbcRatingKeys = useMemo(() => [
    'lowLevelProspect', 'midLevelProspect', 'goodLevelProspect', 'topProspect', 'futureStar',
    'ebaLow', 'ebaHigh', 'lebLow', 'lebHigh', 'acbRotation',
    'bottomBclEurocup', 'midBclEurocup', 'topBclEurocup', 'elMid', 'elTop',
    'nbaRotation', 'nbaAllStar', 'collegeLow', 'collegeMid', 'collegeTop'
  ], []);

  // Fetch informe data
  useEffect(() => {
    const fetchInformeData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/api/informe', {
          params: { playerId },
        });

        setInformeData(response.data);

        if (onDataLoaded) {
          onDataLoaded(response.data.length > 0);
        }

        // Auto-open first informe
        if (response.data.length > 0) {
          setOpenRows({ [response.data[0].id]: true });
        }
      } catch (error) {
        console.error('Error fetching informe data:', error);
        setError(t('playerCardCreator.informeTab.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    fetchInformeData();
  }, [playerId, backendUrl, section, onDataLoaded]);

  // Fetch teams
  useEffect(() => {
    const loadTeams = async () => {
      const _teams = await fetchTeams();
      setTeams(_teams);
    };
    loadTeams();
  }, []);

  // Fetch eventos
  useEffect(() => {
    const loadEventos = async () => {
      const _eventos = await fetchEventos();
      setEventos(_eventos);
    };
    loadEventos();
  }, []);

  // Fetch existing equipos
  useEffect(() => {
    const loadEquipos = async () => {
      const _equipos = await fetchExistingEquipos();
      setExistingEquipos(_equipos);
    };
    loadEquipos();
  }, []);

  // No longer fetching competitions structure - using simple equipo field

  // Generate preview URL for edited media file
  useEffect(() => {
    if (editedMediaFile && editedMediaFile.type?.startsWith('image/')) {
      const url = URL.createObjectURL(editedMediaFile);
      setEditedMediaPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setEditedMediaPreviewUrl(null);
    }
  }, [editedMediaFile]);

  // Paste handler for screenshots in edit dialog
  useEffect(() => {
    if (!openEditDialog) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Rename file with timestamp
            const extension = file.type.split('/')[1] || 'png';
            const newFile = new File([file], `screenshot_${Date.now()}.${extension}`, {
              type: file.type
            });
            setEditedMediaFile(newFile);
            setRemoveExistingMedia(false);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [openEditDialog]);

  // Handlers
  const toggleRow = useCallback((rowId) => {
    setOpenRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const handleEditClick = useCallback((informe) => {
    setCurrentInforme(informe);

    // Handle date with timezone
    if (informe.fecha) {
      let dateObj = new Date(informe.fecha);
      dateObj = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
      const isoDate = dateObj.toISOString().split('T')[0];
      setEditedDate(isoDate);
    } else {
      setEditedDate('');
    }

    setEditedDetalles(informe.detalles || '');
    setEditedEvento(informe.evento || '');
    setEditedEquipo(informe.equipo || '');
    setEditedVisu(informe.visu || '');
    // Normalize nivel and projection to match select option values
    setEditedNivel(normalizeToSelectValue(informe.nivel || ''));
    setEditedProjection(normalizeToSelectValue(informe.projection || ''));
    // Reset media states
    setEditedMediaFile(null);
    setEditedMediaPreviewUrl(null);
    setRemoveExistingMedia(false);
    // FIT values
    setEditedFitEstilo(informe.fit_estilo !== null && informe.fit_estilo !== undefined ? Boolean(informe.fit_estilo) : null);
    setEditedFitFisico(informe.fit_fisico !== null && informe.fit_fisico !== undefined ? Boolean(informe.fit_fisico) : null);
    setEditedFitActitud(informe.fit_actitud !== null && informe.fit_actitud !== undefined ? Boolean(informe.fit_actitud) : null);
    setEditedFitMejora(informe.fit_mejora !== null && informe.fit_mejora !== undefined ? Boolean(informe.fit_mejora) : null);
    setEditedFitContexto(informe.fit_contexto !== null && informe.fit_contexto !== undefined ? Boolean(informe.fit_contexto) : null);
    setEditedStatsUrl(informe.stats_url || '');
    setEditedDefensiveVersatility(normalizeVersatility(informe.defensive_versatility));

    setOpenEditDialog(true);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!currentInforme) return;

    setSaving(true);
    const transformedDetalles = editedDetalles
      .replace(/<p><br><\/p>/g, '<br>')
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '<br>')
      .replace(/<br><br>/g, '<br>');
    const equipoToSave = editedEquipo;

    try {
      let response;
      let newMediaFilename = currentInforme.media_filename;

      // Use FormData if there's a new media file or if we need to remove existing media
      if (editedMediaFile || removeExistingMedia) {
        const formData = new FormData();
        formData.append('detalles', transformedDetalles);
        formData.append('fecha', editedDate);
        formData.append('evento', editedEvento);
        formData.append('equipo', equipoToSave);
        formData.append('visu', editedVisu);
        formData.append('nivel', editedNivel);
        formData.append('projection', editedProjection);
        if (editedFitEstilo !== null) formData.append('fit_estilo', editedFitEstilo ? '1' : '0');
        else formData.append('fit_estilo', '');
        if (editedFitFisico !== null) formData.append('fit_fisico', editedFitFisico ? '1' : '0');
        else formData.append('fit_fisico', '');
        if (editedFitActitud !== null) formData.append('fit_actitud', editedFitActitud ? '1' : '0');
        else formData.append('fit_actitud', '');
        if (editedFitMejora !== null) formData.append('fit_mejora', editedFitMejora ? '1' : '0');
        else formData.append('fit_mejora', '');
        if (editedFitContexto !== null) formData.append('fit_contexto', editedFitContexto ? '1' : '0');
        else formData.append('fit_contexto', '');
        formData.append('stats_url', editedStatsUrl || '');
        formData.append('defensive_versatility', editedDefensiveVersatility ? JSON.stringify(editedDefensiveVersatility) : '');

        if (editedMediaFile) {
          formData.append('media', editedMediaFile);
        }
        if (removeExistingMedia) {
          formData.append('removeMedia', 'true');
        }

        response = await api.put(
          `/api/informe/${currentInforme.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        // Update media filename from response
        if (response.data.mediaFilename !== undefined) {
          newMediaFilename = response.data.mediaFilename;
        } else if (removeExistingMedia && !editedMediaFile) {
          newMediaFilename = null;
        }
      } else {
        const updateData = {
          detalles: transformedDetalles,
          fecha: editedDate,
          evento: editedEvento,
          equipo: equipoToSave,
          visu: editedVisu,
          nivel: editedNivel,
          projection: editedProjection,
          fit_estilo: editedFitEstilo !== null ? (editedFitEstilo ? 1 : 0) : null,
          fit_fisico: editedFitFisico !== null ? (editedFitFisico ? 1 : 0) : null,
          fit_actitud: editedFitActitud !== null ? (editedFitActitud ? 1 : 0) : null,
          fit_mejora: editedFitMejora !== null ? (editedFitMejora ? 1 : 0) : null,
          fit_contexto: editedFitContexto !== null ? (editedFitContexto ? 1 : 0) : null,
          stats_url: editedStatsUrl || null,
          defensive_versatility: editedDefensiveVersatility || null,
        };

        response = await api.put(
          `/api/informe/${currentInforme.id}`,
          updateData
        );
      }

      console.log('Update response:', response.data);

      setInformeData((prev) =>
        prev.map((item) =>
          item.id === currentInforme.id
            ? {
                ...item,
                detalles: transformedDetalles,
                fecha: editedDate,
                evento: editedEvento,
                equipo: equipoToSave,
                visu: editedVisu,
                nivel: editedNivel,
                projection: editedProjection,
                media_filename: newMediaFilename,
                fit_estilo: editedFitEstilo !== null ? (editedFitEstilo ? 1 : 0) : null,
                fit_fisico: editedFitFisico !== null ? (editedFitFisico ? 1 : 0) : null,
                fit_actitud: editedFitActitud !== null ? (editedFitActitud ? 1 : 0) : null,
                fit_mejora: editedFitMejora !== null ? (editedFitMejora ? 1 : 0) : null,
                fit_contexto: editedFitContexto !== null ? (editedFitContexto ? 1 : 0) : null,
                stats_url: editedStatsUrl || null,
                defensive_versatility: editedDefensiveVersatility || null
              }
            : item
        )
      );

      if (editedEvento && !eventos.includes(editedEvento)) {
        setEventos((prev) => [...prev, editedEvento]);
      }

      const updatedEventos = await fetchEventos();
      setEventos(updatedEventos);

      setOpenEditDialog(false);

      // Show success message
      if (response.data && response.data.message) {
        console.log('Success:', response.data.message);
      }
    } catch (error) {
      console.error('Error updating informe:', error);
      console.error('Error details:', error.response?.data);
      setError(t('playerCardCreator.informeTab.errorUpdating') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  }, [currentInforme, editedDetalles, editedDate, editedEvento, editedEquipo, editedVisu, editedNivel, editedProjection, editedMediaFile, removeExistingMedia, editedFitEstilo, editedFitActitud, editedFitMejora, editedFitContexto, editedStatsUrl, editedDefensiveVersatility, eventos, t]);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setCurrentInforme(null);
    setEditedMediaFile(null);
    setEditedMediaPreviewUrl(null);
    setRemoveExistingMedia(false);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  }, []);

  const handleDeleteClick = useCallback((informe) => {
    setInformeToDelete(informe);
    setOpenDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!informeToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/api/informe/${informeToDelete.id}`);
      setInformeData((prev) => prev.filter((item) => item.id !== informeToDelete.id));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting informe:', error);
      setError(t('playerCardCreator.informeTab.errorDeleting'));
    } finally {
      setDeleting(false);
    }
  }, [informeToDelete, backendUrl, section, t]);

  const handleDeleteCancel = useCallback(() => {
    setOpenDeleteDialog(false);
    setInformeToDelete(null);
  }, []);

  // No competition selection handlers needed - using simple equipo field

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <InformeSkeleton />
        <InformeSkeleton />
        <InformeSkeleton />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      </Box>
    );
  }

  // Render empty state
  if (!informeData.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('playerCardCreator.informeTab.noReportsTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('playerCardCreator.informeTab.noReportsDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {informeData.map((row, index) => (
          <Grow in timeout={300} key={row.id} style={{ transitionDelay: `${index * 50}ms` }}>
            <StyledCard>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <HeaderBox onClick={() => toggleRow(row.id)}>
                  {/* Left section with avatar and info */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      flexGrow: 1,
                      minWidth: 0,
                      gap: 2,
                    }}
                  >
                    <Avatar
                      src={getAvatarUrl(row.usuario_id)}
                      alt={row.usuario_nombre}
                      sx={{
                        width: 48,
                        height: 48,
                        border: `2px solid ${theme.palette.divider}`,
                      }}
                    />

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      {/* User name and date */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {row.usuario_nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconWrapper>
                            <CalendarTodayIcon sx={{ fontSize: 14 }} />
                          </IconWrapper>
                          {new Date(row.fecha).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Typography>
                        {dateNaissance && (
                          <AgeAtInformeChip
                            dateNaissance={dateNaissance}
                            informeDate={row.fecha}
                          />
                        )}
                        {row.clip_count > 0 && (
                          <Tooltip title={t('playerCardCreator.informeTab.clips.badgeTooltip', { count: row.clip_count })}>
                            <Chip
                              icon={<VideocamIcon sx={{ fontSize: 14 }} />}
                              label={row.clip_count}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                '& .MuiChip-icon': { color: 'primary.main', ml: 0.5 },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Event */}
                      {row.evento && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
                        >
                          <IconWrapper>
                            <SportsBasketballIcon sx={{ fontSize: 16 }} />
                          </IconWrapper>
                          {row.evento}
                        </Typography>
                      )}

                      {/* Team */}
                      {row.equipo && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
                        >
                          <IconWrapper>
                            <GroupsIcon sx={{ fontSize: 16 }} />
                          </IconWrapper>
                          {row.equipo}
                        </Typography>
                      )}

                      {/* Chips */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {row.visu && (
                          <StyledChip
                            icon={getScoutingIcon(row.visu)}
                            label={getScoutingMethod(row.visu)}
                            size="small"
                            variant="scouting"
                          />
                        )}
                        {row.nivel && (
                          <StyledChip
                            icon={<SignalCellularAltIcon sx={{ fontSize: 16 }} />}
                            label={getTranslatedLevel(row.nivel, t)}
                            size="small"
                            variant="nivel"
                          />
                        )}
                        {row.projection && (
                          <StyledChip
                            icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                            label={getTranslatedLevel(row.projection, t)}
                            size="small"
                            variant="projection"
                          />
                        )}
                        {row.stats_url && (
                          <Chip
                            icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            label={t('playerCardCreator.informeForm.statsUrl')}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatsDialogUrl(row.stats_url);
                              setIframeError(false);
                              setStatsDialogOpen(true);
                            }}
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                              backgroundColor: '#e3f2fd', color: '#1565c0',
                              border: '1px solid #1976d2',
                              '&:hover': { backgroundColor: '#bbdefb' }
                            }}
                          />
                        )}
                      </Stack>

                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: { xs: 2, sm: 0 } }}>
                    {canModifyRecord(currentUser, row.usuario_id) && (
                      <>
                        <Tooltip title={t('playerCardCreator.informeTab.editReport')}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(row);
                            }}
                            size="small"
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('playerCardCreator.informeTab.deleteReport')}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(row);
                            }}
                            size="small"
                            sx={{
                              color: 'error.main',
                              '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

                    <Tooltip title={openRows[row.id] ? t('playerCardCreator.informeTab.collapse') : t('playerCardCreator.informeTab.expand')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(row.id);
                        }}
                        size="small"
                        sx={{
                          transform: openRows[row.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </HeaderBox>

                {/* FIT Club - ligne pleine largeur */}
                {(row.fit_estilo !== null || row.fit_fisico !== null || row.fit_actitud !== null || row.fit_mejora !== null || row.fit_contexto !== null) && (
                  (() => {
                    const fitItems = [
                      { val: row.fit_estilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                      { val: row.fit_fisico, label: t('playerCardCreator.informeForm.fitFisico') },
                      { val: row.fit_actitud, label: t('playerCardCreator.informeForm.fitActitud') },
                      { val: row.fit_mejora, label: t('playerCardCreator.informeForm.fitMejora') },
                      { val: row.fit_contexto, label: t('playerCardCreator.informeForm.fitContexto') }
                    ].filter(f => f.val !== null && f.val !== undefined);
                    const fitYes = fitItems.filter(f => f.val).length;
                    const fitTotal = fitItems.length;
                    const fitPercent = Math.round((fitYes / fitTotal) * 100);
                    const fitColor = fitPercent >= 80 ? '#4caf50' : fitPercent >= 50 ? '#ff9800' : '#f44336';
                    return (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ px: 2.5, pb: 1.5, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            FIT {clubName}:
                          </Typography>
                          <Box sx={{ width: 36, height: 5, borderRadius: 3, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
                            <Box sx={{ width: `${fitPercent}%`, height: '100%', borderRadius: 3, bgcolor: fitColor }} />
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: fitColor, fontSize: '0.7rem' }}>
                            {fitYes}/{fitTotal}
                          </Typography>
                        </Box>
                        {fitItems.map((f, i) => (
                          <Chip
                            key={i}
                            label={`${f.label} ${f.val ? '✓' : '✗'}`}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 600,
                              backgroundColor: f.val ? '#e8f5e9' : '#ffebee',
                              color: f.val ? '#2e7d32' : '#c62828',
                              border: '1px solid',
                              borderColor: f.val ? '#4caf50' : '#f44336'
                            }}
                          />
                        ))}
                      </Stack>
                    );
                  })()
                )}

                {/* Collapsible content */}
                <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                  <ContentBox>
                    {/* Bloc — Versatilidad Defensiva */}
                    {hasAnyValue(normalizeVersatility(row.defensive_versatility)) && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <DefensiveVersatilityChart value={row.defensive_versatility} showTitle />
                      </Paper>
                    )}

                    {/* Bloc — Informe (détalles + média) */}
                    {(row.detalles || row.media_filename) && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mt: hasAnyValue(normalizeVersatility(row.defensive_versatility)) ? 2 : 0,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                        <DescriptionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t('rechercheInforme.informeTitle')}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: row.detalles }}
                        sx={{
                          '& p': { marginBottom: 1.5 },
                          '& br': { display: 'block', content: '""', marginTop: 0.5 },
                        }}
                      />

                      {/* Media attachment display */}
                      {row.media_filename && (
                        <Box sx={{ mt: 2 }}>
                        {isImageMedia(row.media_filename) ? (
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-block',
                              cursor: 'pointer',
                              '&:hover .media-overlay': {
                                opacity: 1
                              }
                            }}
                            onClick={() => setMediaPreview({
                              open: true,
                              url: getInformeMediaUrl(row.media_filename),
                              filename: row.media_filename,
                              informeId: row.id
                            })}
                          >
                            <Box
                              component="img"
                              src={getInformeMediaUrl(row.media_filename)}
                              alt="Media"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                              }}
                            />
                            <Box
                              className="media-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.4)',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s'
                              }}
                            >
                              <OpenInNewIcon sx={{ color: 'white', fontSize: 32 }} />
                            </Box>
                          </Box>
                        ) : isPdfMedia(row.media_filename) ? (
                          <Button
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            endIcon={<OpenInNewIcon />}
                            onClick={() => window.open(getInformeMediaUrl(row.media_filename), '_blank')}
                            sx={{
                              borderColor: '#f44336',
                              color: '#f44336',
                              '&:hover': {
                                borderColor: '#d32f2f',
                                backgroundColor: alpha('#f44336', 0.05)
                              }
                            }}
                          >
                            {t('playerCardCreator.informeTab.viewPdf')}
                          </Button>
                        ) : isHtmlMedia(row.media_filename) ? (
                          <InformeHtmlThumbnail
                            informeId={row.id}
                            onOpen={() => setHtmlPreview(row)}
                          />
                        ) : (
                          <Chip
                            icon={<AttachFileIcon />}
                            label={row.media_filename}
                            onClick={() => window.open(getInformeMediaUrl(row.media_filename), '_blank')}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </Box>
                    )}
                    </Paper>
                    )}

                    {/* Bloc — Clips vidéo liés */}
                    <InformeClipsSection
                      informeId={row.id}
                      expanded={!!openRows[row.id]}
                    />
                  </ContentBox>
                </Collapse>
              </CardContent>
            </StyledCard>
          </Grow>
        ))}

        {/* Edit Dialog */}
        <StyledDialog
          open={openEditDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseEditDialog();
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6">{t('playerCardCreator.informeTab.editDialogTitle')}</Typography>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3 }}>
            {currentInforme && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('playerCardCreator.informeTab.date')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={editedDate}
                      onChange={(e) => setEditedDate(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      freeSolo
                      options={eventos}
                      value={editedEvento}
                      onChange={(_, newValue) => setEditedEvento(newValue || '')}
                      onInputChange={(event, newValue) => {
                        if (event && event.type === 'change') {
                          setEditedEvento(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('playerCardCreator.informeTab.event')}
                          variant="outlined"
                          placeholder={t('playerCardCreator.informeTab.selectOrType')}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      freeSolo
                      options={[...new Set([...(existingEquipos || []), ...(teams || [])])].sort()}
                      value={editedEquipo}
                      onChange={(_, newValue) => setEditedEquipo(newValue || '')}
                      onInputChange={(event, newValue) => {
                        if (event && event.type === 'change') {
                          setEditedEquipo(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('playerCardCreator.informeTab.team')}
                          variant="outlined"
                          placeholder={t('playerCardCreator.informeTab.selectOrTypeTeam')}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('playerCardCreator.informeTab.scoutingMethod')}
                      value={editedVisu}
                      onChange={(e) => setEditedVisu(e.target.value)}
                      fullWidth
                      variant="outlined"
                    >
                      <MenuItem value="">
                        <em>{t('playerCardCreator.informeTab.none')}</em>
                      </MenuItem>
                      <MenuItem value="video">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VideocamIcon fontSize="small" /> {t('playerCardCreator.informeTab.video')}
                        </Box>
                      </MenuItem>
                      <MenuItem value="live">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RemoveRedEyeIcon fontSize="small" /> {t('playerCardCreator.informeTab.live')}
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('playerCardCreator.informeTab.currentLevel')}
                      value={editedNivel}
                      onChange={(e) => setEditedNivel(e.target.value)}
                      fullWidth
                      variant="outlined"
                    >
                      <MenuItem value="">
                        <em>{t('playerCardCreator.informeTab.none')}</em>
                      </MenuItem>
                      {vbcRatingKeys.map((key) => (
                        <MenuItem key={key} value={key}>
                          {t(`createPlayer.levels.${key}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('playerCardCreator.informeTab.futureProjection')}
                      value={editedProjection}
                      onChange={(e) => setEditedProjection(e.target.value)}
                      fullWidth
                      variant="outlined"
                    >
                      <MenuItem value="">
                        <em>{t('playerCardCreator.informeTab.none')}</em>
                      </MenuItem>
                      {vbcRatingKeys.map((key) => (
                        <MenuItem key={key} value={key}>
                          {t(`createPlayer.levels.${key}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Stats URL */}
                  <Grid item xs={12}>
                    <TextField
                      label={t('playerCardCreator.informeForm.statsUrl')}
                      value={editedStatsUrl}
                      onChange={(e) => setEditedStatsUrl(e.target.value)}
                      placeholder="https://..."
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  {/* FIT Club */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 1 }}>
                        FIT {clubName} :
                      </Typography>
                      {[
                        { key: 'fitEstilo', value: editedFitEstilo, setter: setEditedFitEstilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                        { key: 'fitFisico', value: editedFitFisico, setter: setEditedFitFisico, label: t('playerCardCreator.informeForm.fitFisico') },
                        { key: 'fitActitud', value: editedFitActitud, setter: setEditedFitActitud, label: t('playerCardCreator.informeForm.fitActitud') },
                        { key: 'fitMejora', value: editedFitMejora, setter: setEditedFitMejora, label: t('playerCardCreator.informeForm.fitMejora') },
                        { key: 'fitContexto', value: editedFitContexto, setter: setEditedFitContexto, label: t('playerCardCreator.informeForm.fitContexto') }
                      ].map(({ key, value, setter, label }) => (
                        <Chip
                          key={key}
                          label={`${label}: ${value === true ? t('playerCardCreator.informeForm.fitYes') : value === false ? t('playerCardCreator.informeForm.fitNo') : '—'}`}
                          onClick={() => {
                            if (value === null) setter(true);
                            else if (value === true) setter(false);
                            else setter(null);
                          }}
                          sx={{
                            cursor: 'pointer', fontWeight: 600,
                            backgroundColor: value === true ? '#e8f5e9' : value === false ? '#ffebee' : '#f5f5f5',
                            color: value === true ? '#2e7d32' : value === false ? '#c62828' : '#9e9e9e',
                            border: '1px solid',
                            borderColor: value === true ? '#4caf50' : value === false ? '#f44336' : '#e0e0e0',
                            '&:hover': { opacity: 0.85 }
                          }}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Defensive Versatility */}
                  <Grid item xs={12}>
                    <DefensiveVersatilityEditor
                      value={editedDefensiveVersatility}
                      onChange={setEditedDefensiveVersatility}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                      {t('playerCardCreator.informeTab.reportDetails')}
                    </Typography>
                    <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                      <ReactQuill
                        theme="snow"
                        value={editedDetalles}
                        onChange={setEditedDetalles}
                        placeholder={t('playerCardCreator.informeTab.writeDetails')}
                        style={{ minHeight: '200px' }}
                      />
                    </Paper>
                  </Grid>

                  {/* Media Upload Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon sx={{ fontSize: 18 }} />
                      {t('playerCardCreator.informeForm.attachMedia')}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        ({t('playerCardCreator.informeForm.optional')})
                      </Typography>
                    </Typography>

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={editFileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (!isAcceptedMedia(file)) {
                            alert(t('playerCardCreator.informeForm.invalidFileType'));
                            return;
                          }
                          if (file.size > MAX_MEDIA_SIZE) {
                            alert(t('playerCardCreator.informeForm.fileTooLarge'));
                            return;
                          }
                          setEditedMediaFile(file);
                          setRemoveExistingMedia(false);
                        }
                      }}
                      accept={MEDIA_ACCEPT_ATTR}
                      style={{ display: 'none' }}
                    />

                    {/* Show current media if exists and not removed */}
                    {currentInforme?.media_filename && !removeExistingMedia && !editedMediaFile && (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          backgroundColor: 'grey.50'
                        }}
                      >
                        {isImageMedia(currentInforme.media_filename) ? (
                          <Box
                            component="img"
                            src={getInformeMediaUrl(currentInforme.media_filename)}
                            alt="Media"
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}
                          />
                        ) : isHtmlMedia(currentInforme.media_filename) ? (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#e67e22',
                              borderRadius: 1
                            }}
                          >
                            <CodeIcon sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f44336',
                              borderRadius: 1
                            }}
                          >
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {currentInforme.media_filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.informeTab.currentMedia')}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => editFileInputRef.current?.click()}
                          >
                            {t('playerCardCreator.informeTab.changeMedia')}
                          </Button>
                          <IconButton
                            onClick={() => setRemoveExistingMedia(true)}
                            size="small"
                            sx={{
                              color: 'error.main',
                              '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Box>
                    )}

                    {/* Show new media file preview */}
                    {editedMediaFile && (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }}
                      >
                        {editedMediaPreviewUrl ? (
                          <Box
                            component="img"
                            src={editedMediaPreviewUrl}
                            alt="Preview"
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}
                          />
                        ) : editedMediaFile.type === 'application/pdf' ? (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f44336',
                              borderRadius: 1
                            }}
                          >
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                        ) : isHtmlFile(editedMediaFile) ? (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#e67e22',
                              borderRadius: 1
                            }}
                          >
                            <CodeIcon sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.200',
                              borderRadius: 1
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {editedMediaFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(editedMediaFile.size / 1024).toFixed(1)} KB • {t('playerCardCreator.informeTab.newMedia')}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => {
                            setEditedMediaFile(null);
                            if (editFileInputRef.current) editFileInputRef.current.value = '';
                          }}
                          size="small"
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}

                    {/* Show upload zone if no media */}
                    {((!currentInforme?.media_filename || removeExistingMedia) && !editedMediaFile) && (
                      <Box
                        onClick={() => editFileInputRef.current?.click()}
                        sx={{
                          border: '2px dashed',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: 'grey.50',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('playerCardCreator.informeForm.dropOrClick')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('playerCardCreator.informeForm.pasteHint')}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            JPG, PNG, GIF, WebP, PDF • Max 10MB
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={handleCloseEditDialog}
              color="inherit"
              disabled={saving}
            >
              {t('playerCardCreator.informeTab.cancel')}
            </Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              disabled={saving}
              startIcon={saving && <CircularProgress size={16} />}
            >
              {saving ? t('playerCardCreator.informeTab.saving') : t('playerCardCreator.informeTab.saveChanges')}
            </Button>
          </DialogActions>
        </StyledDialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Typography variant="h6">{t('playerCardCreator.informeTab.deleteDialogTitle')}</Typography>
          </DialogTitle>

          <DialogContent>
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
              {t('playerCardCreator.informeTab.deleteWarning')}
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              color="inherit"
              disabled={deleting}
            >
              {t('playerCardCreator.informeTab.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting && <CircularProgress size={16} color="inherit" />}
            >
              {deleting ? t('playerCardCreator.informeTab.deleting') : t('playerCardCreator.informeTab.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Aperçu HTML — iframe sandbox, mutualisé avec les documents joueurs */}
        <DocumentMediaDialog
          open={!!htmlPreview}
          doc={htmlPreview ? asMediaDoc(htmlPreview) : null}
          fileUrl={htmlPreview ? getInformeMediaUrl(htmlPreview.media_filename) : null}
          onClose={() => setHtmlPreview(null)}
        />

        {/* Media Preview Dialog */}
        <Dialog
          open={mediaPreview.open}
          onClose={() => setMediaPreview({ open: false, url: '', filename: '', informeId: null })}
          maxWidth="lg"
          PaperProps={{
            sx: { borderRadius: 2, backgroundColor: 'transparent', boxShadow: 'none' }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={() => setMediaPreview({ open: false, url: '', filename: '', informeId: null })}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)'
                },
                zIndex: 1
              }}
            >
              <CloseIcon />
            </IconButton>
            {mediaPreview.url && (
              <Box
                component="img"
                src={mediaPreview.url}
                alt="Media preview"
                sx={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: 2
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Stats URL iframe dialog */}
        <Dialog
          open={statsDialogOpen}
          onClose={() => setStatsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '90vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
              {t('playerCardCreator.informeForm.statsUrl')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(statsDialogUrl, '_blank')}
              >
                {t('playerCardCreator.informeForm.openInNewTab')}
              </Button>
              <IconButton onClick={() => setStatsDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {iframeError ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <Typography color="text.secondary">
                  {t('playerCardCreator.informeForm.iframeBlocked')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(statsDialogUrl, '_blank')}
                >
                  {t('playerCardCreator.informeForm.openInNewTab')}
                </Button>
              </Box>
            ) : (
              <iframe
                src={statsDialogUrl}
                title="Stats"
                style={{ width: '100%', height: '100%', border: 'none' }}
                onError={() => setIframeError(true)}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            )}
          </DialogContent>
        </Dialog>

      </Box>
    </Fade>
  );
};

export default InformeTab;